import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ScrollToTop from '../common/ScrollToTop';
import ReassignPersonDialog from './ReassignPersonDialog';
import { useAuth } from '../../context/AuthContext';
import { getStudyTeamByProject, upsertStudyTeam, reassignStudyTeamPerson } from '../../services/studyTeam.service';
import { fetchActiveUsersDirectory } from '../../services/userDirectory.service';

/** listId (used for client-side duplicate detection) -> backend Study Team role key. */
const LIST_ID_TO_ROLE = {
  admins: 'author_admins',
  coordinators: 'coordinators',
  coAuthors: 'co_authors',
  reviewers: 'reviewers',
  approvers: 'approvers',
};

const ROLE_LABELS = {
  author_admins: 'Administrator',
  coordinators: 'Coordinator',
  co_authors: 'Co-Author',
  reviewers: 'Reviewer',
  approvers: 'Approver',
};

const MAX_CO_AUTHORS = 5;
const MAX_REVIEWERS = 25;

const emptyPerson = () => ({ name: '', email: '', userId: undefined });

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

/** Identity keys for duplicate detection: track both userId and email so manual vs directory match. */
function rowIdentityKeys(row) {
  const keys = new Set();
  if (row.userId != null && row.userId !== '') keys.add(`uid:${String(row.userId)}`);
  const em = normalizeEmail(row.email);
  if (em) keys.add(`em:${em}`);
  return keys;
}

function rowHasIdentity(row) {
  return rowIdentityKeys(row).size > 0;
}

/** Occupied identities within one role list only (same user may appear in multiple roles). */
function buildOccupiedKeysForRole(allSections, listId, excludeIndex) {
  const occupied = new Set();
  const section = allSections.find((s) => s.listId === listId);
  if (!section) return occupied;
  section.rows.forEach((row, idx) => {
    if (idx === excludeIndex) return;
    rowIdentityKeys(row).forEach((k) => occupied.add(k));
  });
  return occupied;
}

function nextRowConflictsOthers(nextRow, occupied) {
  if (!rowHasIdentity(nextRow)) return false;
  for (const k of rowIdentityKeys(nextRow)) {
    if (occupied.has(k)) return true;
  }
  return false;
}

function directoryOptionsForRow(directoryUsers, occupied, row) {
  if (!directoryUsers.length) return [];
  return directoryUsers.filter((u) => {
    if (row.userId != null && String(row.userId) === String(u.id)) return true;
    const candKeys = rowIdentityKeys({ userId: u.id, email: u.email });
    for (const k of candKeys) {
      if (occupied.has(k)) return false;
    }
    return true;
  });
}

function defaultAuthorFromUser(user) {
  if (!user) return emptyPerson();
  const name =
    user.name ||
    [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
    user.fullName ||
    '';
  return {
    name,
    email: user.email || '',
    userId: user.id || user.userId,
  };
}

function PersonRowEditor({ row, onChange, onRemove, onReassign, readOnly, directoryUsers, pickerOptions }) {
  const baseOpts = pickerOptions ?? directoryUsers;
  const matchFromDirectory =
    row.userId != null
      ? directoryUsers.find((u) => String(u.id) === String(row.userId))
      : directoryUsers.find((u) => row.email && u.email.toLowerCase() === String(row.email).toLowerCase());

  const optionIds = new Set(baseOpts.map((u) => String(u.id)));
  const autocompleteOptions =
    matchFromDirectory && !optionIds.has(String(matchFromDirectory.id))
      ? [matchFromDirectory, ...baseOpts]
      : baseOpts;

  return (
    <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
      {directoryUsers.length > 0 && (
        <Grid item xs={12}>
          <Autocomplete
            size="small"
            disabled={readOnly}
            options={autocompleteOptions}
            value={matchFromDirectory || null}
            onChange={(_, opt) => {
              if (!opt) {
                onChange({ name: '', email: '', userId: undefined });
                return;
              }
              onChange({
                name: opt.name || '',
                email: opt.email || '',
                userId: opt.id,
              });
            }}
            getOptionLabel={(opt) =>
              opt?.email ? `${opt.name || '(no name)'} (${opt.email})` : opt?.name || ''
            }
            isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select user"
                placeholder="Search by name or email"
              />
            )}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={5}>
        <TextField
          fullWidth
          size="small"
          label="Name"
          value={row.name}
          onChange={(e) => onChange({ ...row, name: e.target.value })}
          disabled={readOnly}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Email"
          type="email"
          value={row.email}
          onChange={(e) => onChange({ ...row, email: e.target.value, userId: undefined })}
          disabled={readOnly}
        />
      </Grid>
      <Grid item xs={12} sm={1}>
        {!readOnly && rowHasIdentity(row) && (
          <IconButton aria-label="reassign" title="Reassign" onClick={onReassign} size="small">
            <SwapHorizIcon fontSize="small" />
          </IconButton>
        )}
        {!readOnly && (
          <IconButton aria-label="remove" onClick={onRemove} size="small">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
}

function RoleList({
  title,
  rows,
  setRows,
  readOnly,
  directoryUsers,
  maxRows,
  listId,
  allSections,
  onDuplicateAttempt,
  onReassignRow,
}) {
  const atMax = typeof maxRows === 'number' && rows.length >= maxRows;
  const canAddMore = !readOnly && !atMax;

  const applyChange = (idx, next) => {
    const occupied = buildOccupiedKeysForRole(allSections, listId, idx);
    if (nextRowConflictsOthers(next, occupied)) {
      onDuplicateAttempt();
      return;
    }
    setRows((prev) => prev.map((r, i) => (i === idx ? next : r)));
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
        {typeof maxRows === 'number' && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
            (max {maxRows})
          </Typography>
        )}
      </Typography>
      {rows.map((row, idx) => {
        const occupied = buildOccupiedKeysForRole(allSections, listId, idx);
        const pickerOptions = directoryOptionsForRow(directoryUsers, occupied, row);
        return (
          <PersonRowEditor
            key={idx}
            row={row}
            readOnly={readOnly}
            directoryUsers={directoryUsers}
            pickerOptions={pickerOptions}
            onChange={(next) => applyChange(idx, next)}
            onRemove={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
            onReassign={() => onReassignRow(idx, row)}
          />
        );
      })}
      {readOnly && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No entries.
        </Typography>
      )}
      {canAddMore && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            setRows((prev) => {
              if (typeof maxRows === 'number' && prev.length >= maxRows) return prev;
              return [...prev, emptyPerson()];
            })
          }
        >
          Add
        </Button>
      )}
    </Box>
  );
}

const StudyTeamForm = ({ projectId, embedded }) => {
  const { user, canManageStudyTeam: canEdit } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [authorAdmins, setAuthorAdmins] = useState([emptyPerson()]);
  const [coordinators, setCoordinators] = useState([]);
  const [coAuthors, setCoAuthors] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [approvers, setApprovers] = useState([]);

  /** Wrap a role-list setState so any add/edit/remove marks the roster unsaved again. */
  const withDirty = (setter) => (updater) => {
    setHasSaved(false);
    setter(updater);
  };
  const [directoryRoster, setDirectoryRoster] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [reassignState, setReassignState] = useState({ open: false, listId: null, roleKey: null, idx: null, person: null });

  const applyStudyTeamData = (d) => {
    if (!d) {
      setAuthorAdmins([defaultAuthorFromUser(user)]);
      setCoordinators([]);
      setCoAuthors([]);
      setReviewers([]);
      setApprovers([]);
      return;
    }
    const admins = Array.isArray(d.author_admins) ? d.author_admins : [];
    setAuthorAdmins(admins.length ? admins.map((p) => ({ ...emptyPerson(), ...p })) : [emptyPerson()]);
    setCoordinators(Array.isArray(d.coordinators) ? d.coordinators.map((p) => ({ ...emptyPerson(), ...p })) : []);
    setCoAuthors(
      Array.isArray(d.co_authors) ? d.co_authors.slice(0, MAX_CO_AUTHORS).map((p) => ({ ...emptyPerson(), ...p })) : []
    );
    setReviewers(
      Array.isArray(d.reviewers) ? d.reviewers.slice(0, MAX_REVIEWERS).map((p) => ({ ...emptyPerson(), ...p })) : []
    );
    setApprovers(Array.isArray(d.approvers) ? d.approvers.map((p) => ({ ...emptyPerson(), ...p })) : []);
  };

  const openReassign = (listId, idx, person) => {
    setReassignState({ open: true, listId, roleKey: LIST_ID_TO_ROLE[listId], idx, person });
  };

  const closeReassign = () => setReassignState((s) => ({ ...s, open: false }));

  const handleReassignConfirm = async (newPerson) => {
    const { roleKey, person } = reassignState;
    const body = await reassignStudyTeamPerson(projectId, { role: roleKey, oldPerson: person, newPerson });
    applyStudyTeamData(body?.data);
    setHasSaved(true);
    closeReassign();
    const synced = typeof body?.syncedWorkflows === 'number' ? body.syncedWorkflows : 0;
    setSnackbar({
      open: true,
      message: `Reassigned ${ROLE_LABELS[roleKey]}.${synced > 0 ? ` Synced to ${synced} document workflow${synced === 1 ? '' : 's'}.` : ''}`,
      severity: 'success',
    });
  };

  useEffect(() => {
    if (!canEdit || !projectId) {
      setDirectoryRoster({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const roster = await fetchActiveUsersDirectory(projectId);
        if (!cancelled) setDirectoryRoster(roster);
      } catch (e) {
        if (!cancelled) {
          console.error('Study team user directory:', e);
          setDirectoryRoster({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canEdit, projectId]);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getStudyTeamByProject(projectId);
        const d = res?.data;
        if (cancelled) return;
        applyStudyTeamData(d);
        setHasSaved(Boolean(d));
      } catch (e) {
        if (!cancelled) {
          setAuthorAdmins([defaultAuthorFromUser(user)]);
          setHasSaved(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, user]);

  const allSections = useMemo(
    () => [
      { listId: 'admins', rows: authorAdmins },
      { listId: 'coordinators', rows: coordinators },
      { listId: 'coAuthors', rows: coAuthors },
      { listId: 'reviewers', rows: reviewers },
      { listId: 'approvers', rows: approvers },
    ],
    [authorAdmins, coordinators, coAuthors, reviewers, approvers]
  );

  const notifyDuplicate = () => {
    setSnackbar({
      open: true,
      message: 'That person is already listed in this role (same user ID or email twice in this section).',
      severity: 'warning',
    });
  };

  const handleSave = async () => {
    if (!projectId || !canEdit) return;
    setSaving(true);
    try {
      const body = await upsertStudyTeam({
        projectId,
        author_admins: authorAdmins,
        coordinators,
        co_authors: coAuthors.slice(0, MAX_CO_AUTHORS),
        reviewers: reviewers.slice(0, MAX_REVIEWERS),
        approvers,
      });
      const synced = typeof body?.syncedWorkflows === 'number' ? body.syncedWorkflows : 0;
      let msg = 'Study team saved.';
      if (synced > 0) {
        msg += ` Linked roles applied to ${synced} document workflow${synced === 1 ? '' : 's'} (co-authors, reviewers, approvers).`;
      }
      setHasSaved(true);
      setSnackbar({ open: true, message: msg, severity: 'success' });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || e.message || 'Save failed',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!projectId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Open a project with <code>?project=...</code> to manage the study team.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
        <CircularProgress size={28} />
        <Typography>Loading study team…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: embedded ? 0 : 2, maxWidth: 900 }}>
      <Typography variant="h6" gutterBottom>
        Study team
      </Typography>
      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Authors, managers, and administrators can edit the study team. Your role is view-only here.
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Anyone listed on this study team (any role) can open this project. System administrators
        (app role) can see all projects; Study Team administrators do not get special project-access
        privileges beyond membership. Use <strong>Select user</strong> to choose someone from the
        user directory (name and email from the database), or type name and email manually for people
        not in the system. Use <strong>Add</strong> when more than one person holds a role. The same
        user may appear in several roles; within each role, avoid listing the same person twice.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Saving assigns{' '}
        <strong>authors + co-authors</strong> to document workflows as co-authors,{' '}
        <strong>project coordinators</strong> to workflow coordinators, <strong>reviewers</strong> as workflow reviewers,
        and <strong>approvers</strong> as workflow approve permission — linked users must have a user ID (picked from the
        directory). New uploads inherit this roster automatically; opening a document workflow refreshes rosters from the
        study team.
      </Typography>

      <RoleList
        title="Administrators (authors)"
        listId="admins"
        allSections={allSections}
        onDuplicateAttempt={notifyDuplicate}
        onReassignRow={(idx, row) => openReassign('admins', idx, row)}
        rows={authorAdmins}
        setRows={withDirty(setAuthorAdmins)}
        readOnly={!canEdit}
        directoryUsers={directoryRoster.author_admins || []}
      />

      <Divider sx={{ my: 2 }} />

      <RoleList
        title="Project coordinators"
        listId="coordinators"
        allSections={allSections}
        onDuplicateAttempt={notifyDuplicate}
        onReassignRow={(idx, row) => openReassign('coordinators', idx, row)}
        rows={coordinators}
        setRows={withDirty(setCoordinators)}
        readOnly={!canEdit}
        directoryUsers={directoryRoster.coordinators || []}
      />
      <RoleList
        title="Co-authors"
        listId="coAuthors"
        allSections={allSections}
        onDuplicateAttempt={notifyDuplicate}
        onReassignRow={(idx, row) => openReassign('coAuthors', idx, row)}
        rows={coAuthors}
        setRows={withDirty(setCoAuthors)}
        readOnly={!canEdit}
        directoryUsers={directoryRoster.co_authors || []}
        maxRows={MAX_CO_AUTHORS}
      />
      <RoleList
        title="Reviewers"
        listId="reviewers"
        allSections={allSections}
        onDuplicateAttempt={notifyDuplicate}
        onReassignRow={(idx, row) => openReassign('reviewers', idx, row)}
        rows={reviewers}
        setRows={withDirty(setReviewers)}
        readOnly={!canEdit}
        directoryUsers={directoryRoster.reviewers || []}
        maxRows={MAX_REVIEWERS}
      />
      <RoleList
        title="Approvers"
        listId="approvers"
        allSections={allSections}
        onDuplicateAttempt={notifyDuplicate}
        onReassignRow={(idx, row) => openReassign('approvers', idx, row)}
        rows={approvers}
        setRows={withDirty(setApprovers)}
        readOnly={!canEdit}
        directoryUsers={directoryRoster.approvers || []}
      />

      {canEdit && (
        <Button variant="contained" onClick={handleSave} disabled={saving || hasSaved} sx={{ mt: 1 }}>
          {saving ? <CircularProgress size={22} /> : hasSaved ? 'Saved' : 'Save study team'}
        </Button>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ReassignPersonDialog
        open={reassignState.open}
        onClose={closeReassign}
        projectId={projectId}
        roleKey={reassignState.roleKey}
        roleLabel={ROLE_LABELS[reassignState.roleKey] || ''}
        currentPerson={reassignState.person}
        occupiedKeys={buildOccupiedKeysForRole(allSections, reassignState.listId, reassignState.idx)}
        onConfirm={handleReassignConfirm}
      />
      <ScrollToTop />
    </Box>
  );
};

export default StudyTeamForm;
